import React, { useEffect, useRef, useState } from 'react';
import { WalletCards, Hospital, SquarePen, X, Save, Loader, Plus } from 'lucide-react';
import { can_visit, notify, numberWithCommas } from "../../../helpers";
import { transitionalCare, updateTransitionalCare } from '../../../services/audits';
import { Drawer } from 'vaul';
import Spacing from '../../Spacing';
import Grid from '../../Grid';

export default function TransitionalCareManagementModal({
  account_id,
  location_id,
  activeModal = false,
  setActiveModal = () => {}
}) {

  const currentDate = new Date();
  const [loadData, setLoadData] = useState(true);
  const [transitionalCareData, setTransitionalCareData] = useState([]);
  const [editTransitionalCare, setEditTransitionalCare] = useState(false);
  const [saveTransitionalCareLoader, setSaveTransitionalCareLoader] = useState(false);
  const [formData, setFormData] = useState({
    account_id: account_id,
    location_id: location_id,
    potential_revenue: 0,
    every_encounter_amount: 0,
    practice_opportunities: []
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    getTransitionalCareData(account_id, location_id);
  }, [account_id, location_id]);

  const getTransitionalCareData = async (accountId, locationId) => {
    try {
      const response = await transitionalCare(accountId, locationId);

      if (response) {
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          setTransitionalCareData(response?.data);
          setTransitionalFormData(response?.data);
          setLoadData(false);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getTransitionalCareData ~ error:", error);
    }
  };

  const setTransitionalFormData = (data) => {
    setFormData({
      account_id: account_id,
      location_id: location_id,
      ...data,
      practice_opportunities: data.practice_opportunities?.map(transitionalCare => ({
        ...transitionalCare
      }))
    });
  };

  const handleAddDataSource = () => {
    const newTransitionalCare = {
      year: currentDate.getFullYear(),
      annual_wellness_completed_visit: 0,
      annual_wellness_completed_visit_percentage: 0,
      potential_annual_wellness_completed_visit: 0
    };

    setFormData((prevData) => ({
      ...prevData,
      practice_opportunities: [...prevData.practice_opportunities, newTransitionalCare]
    }));
  };

  const handleDeleteRecord = async (index) => {
    setFormData((prevData) => {
      const updatedTransitionalCare = prevData.practice_opportunities.filter((_, i) => i !== index);
      return {
        ...prevData,
        practice_opportunities: updatedTransitionalCare
      };
    });
  };

  const handleInputChange = (key, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleTransitionalCareChange = (index, key, value) => {
    const updatedTransitionalCare = formData.practice_opportunities.map((opp, i) => 
      i === index ? { ...opp, [key]: value } : opp
    );

    setFormData((prevData) => ({
      ...prevData,
      practice_opportunities: updatedTransitionalCare
    }));
  };

  const handleCancelEdit = () => {
    setTransitionalFormData(transitionalCareData);
    setEditTransitionalCare(false);
  };

  const updateTransitionalCareReport = async (e) => {
    e.preventDefault();
    setSaveTransitionalCareLoader(true);

    try {
      const response = await updateTransitionalCare(formData);
      if(response){
        if(response?.detail) {
          notify("error", response?.detail);
        } else {
          if(response?.data?.status === 'success') {
            notify("success", response?.data?.message);
            await getTransitionalCareData(account_id, location_id);
            setEditTransitionalCare(false);
          } else  {
            notify("error", response?.data?.message);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 1);
      }
    } finally {
      setSaveTransitionalCareLoader(false);
    }
  }

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={setActiveModal}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Patient Details</Drawer.Title>
            <Drawer.Description className="sr-only">View details of a parient</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">

              <div className="docpro_modal" style={{ backgroundColor: 'rgb(249, 249, 251)' }}>
                {loadData ? (
                  <div
                    style={{
                      height: "300px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    Loading...
                  </div>
                ) : (
                  <>
                    <form onSubmit={updateTransitionalCareReport}>
                      <div className="docpro_page_heading_wrap audit_modal_heading">
                        <h2 className="docpro_page_heading docpro_fs_24">
                          <i
                            className="docpro_white_color"
                            style={{ backgroundColor: "#32aa8e" }}
                          >
                            <Hospital width={18} height={18} />
                          </i>
                          Transitional Care Management
                        </h2>
                        <div className="docpro_page_heading_btns">
                          {can_visit('administrator') && (
                          !editTransitionalCare ?
                            <button 
                              type="button"
                              className="docpro_bordered_btn" 
                              style={{ background: '#1578d5', borderRadius: '5px', color: '#ffffff' }}
                              onClick={()=>{setEditTransitionalCare(true);}}
                            >
                              <SquarePen width={20} height={20} />
                              <span>Edit</span>
                            </button>
                          :
                            <div className="flex item-center docpro_grid_gap_10">
                              <button 
                                type="button"
                                className="docpro_bordered_btn" 
                                style={{ background: '#e5484d', borderRadius: '5px', color: '#ffffff' }}
                                onClick={handleCancelEdit}
                              >
                                <X width={20} height={20} />
                                <span>Cancel</span>
                              </button>
                              {editTransitionalCare && (
                                <button
                                  type="submit"
                                  style={{ borderRadius: '4px' }}
                                  className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
                                  disabled={saveTransitionalCareLoader ? true : false}
                                >
                                  <Save width={18} height={18} />
                                  <span>Save</span>
                                  {saveTransitionalCareLoader && (
                                    <span className="docpro_loading">
                                      <Loader width={20} height={20} />
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <Grid variant="docpro_grid_gap_20 docpro_grid_col_3">
                        <div className="docpro_card docpro_card_md docpro_card_1">
                          <h3 className="docpro_fs_14 docpro_mb_0 docpro_olive_color">
                            <WalletCards /> 
                            Potential Revenue
                          </h3>
                          <Spacing lg="5" sm="5" />
                          <p className="docpro_fw_500 docpro_fs_28 docpro_primary_color docpro_mb_0">
                            {!editTransitionalCare ? 
                              <>${numberWithCommas(transitionalCareData?.potential_revenue? numberWithCommas(transitionalCareData?.potential_revenue) : 0.00)}</>
                            :
                              <span className="docpro_signout_input items-center">
                                $
                                <input
                                  type="text"
                                  className="docpro_fw_500"
                                  style={{ maxWidth: "160px" }}
                                  defaultValue={formData?.potential_revenue || 0}
                                  onChange={(e) => handleInputChange("potential_revenue", parseFloat(e.target.value))}
                                />
                              </span>
                            }
                          </p>
                        </div>
                      </Grid>
                      <Spacing lg="18" sm="18" />
                      <div>
                        <h3 className='docpro_fs_20 docpro_mb_0 mb-1'>Practice opportunities</h3>
                        <p className='docpro_fs_14 docpro_mb_0 docpro_olive_color'>Considering $
                          {!editTransitionalCare ? 
                            <>{transitionalCareData?.every_encounter_amount ? transitionalCareData?.every_encounter_amount : 0}</>
                          :
                            <span className="docpro_signout_input">
                              <input
                                type="text"
                                className="docpro_fw_500"
                                style={{ maxWidth: "40px", backgroundColor: "transparent" }}
                                name="every_encounter_amount"
                                defaultValue={formData?.every_encounter_amount || 0}
                                onChange={(e) => handleInputChange("every_encounter_amount", parseFloat(e.target.value))}
                              />
                            </span>
                          }
                          &nbsp; for every encounter</p>
                      </div>
                      <Spacing lg="18" sm="18" />
                      <div className="docpro_data_table_wrap">
                        <div className="docpro_table_responsive" style={{ minHeight: "100px", paddingTop: "6px" }}>
                          <table className="docpro_mb_0 opportunity_table" style={{ height: "100%" }}>
                            <thead>
                              <tr>
                                {editTransitionalCare && (
                                  <th style={{ width: '60px' }}></th>
                                )}
                                <th>Year</th>
                                <th>Annual Wellness Visit (Completed)</th>
                                <th>Your Revenue</th>
                                <th>Potential Annual Wellness Visit</th>
                                <th>Potential Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {!loadData ? (
                                formData?.practice_opportunities?.length > 0 ? (
                                  formData?.practice_opportunities?.map((item, index) => (
                                    <tr key={index}>
                                      {editTransitionalCare && (
                                        <td style={{ padding: 0 }}>
                                          <div className='flex items-center' style={{ justifyContent: "end" }}>
                                            {(index === formData?.practice_opportunities?.length - 1) ? (
                                              <div className="text-right">
                                                <button 
                                                  type="button"
                                                  onClick={handleAddDataSource} 
                                                  className="docpro_icon_btn docpro_fw_600 docpro_primary_color mr-1" 
                                                  style={{ borderRadius: '50%', height: '24px', width: '24px' }}
                                                >
                                                  <Plus width={16} height={16} />
                                                  <span className="docpro_tip">Add</span>
                                                </button>
                                              </div>
                                            ) : (
                                              <div style={{ minWidth: '35px' }}></div>
                                            )}
                                            {formData?.practice_opportunities?.length > 1 && (
                                              <div className="text-right">
                                                <button
                                                  type="button"
                                                  onClick={() => handleDeleteRecord(index)}
                                                  className="docpro_icon_btn docpro_red_color docpro_icon_btn_red "
                                                  style={{ borderRadius: '50%', height: '24px', width: '24px' }}
                                                >
                                                  <X width={16} height={16} />
                                                  <span className="docpro_tip">Remove</span>
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      )}
                                      <td>
                                        {!editTransitionalCare ? 
                                          <>{item?.year || ""}</>
                                        :
                                          <span className="docpro_signout_input">
                                            <input
                                              type="text"
                                              className="docpro_fw_500"
                                              style={{ maxWidth: "50px" }}
                                              value={formData?.practice_opportunities[index]?.year || ""}
                                              onChange={(e) => handleTransitionalCareChange(index, "year", parseInt(e.target.value))}
                                            />
                                          </span>
                                        }
                                      </td>
                                      <td>
                                        <p className='docpro_mb_0 docpro_fs_16'>
                                          {!editTransitionalCare ? 
                                            <>{item?.annual_wellness_completed_visit ? numberWithCommas(item?.annual_wellness_completed_visit) : 0}</>
                                          :
                                            <span className="docpro_signout_input">
                                              <input
                                                type="text"
                                                className="docpro_fw_500"
                                                style={{ maxWidth: "50px" }}
                                                value={formData?.practice_opportunities[index]?.annual_wellness_completed_visit || 0}
                                                onChange={(e) => handleTransitionalCareChange(index, "annual_wellness_completed_visit", parseInt(e.target.value))}
                                              />
                                            </span>
                                          }
                                        </p>
                                        <Spacing lg="5" sm="5" />
                                        <div className="docpro_audit_progress maximizing_progress mb-2">
                                          <div 
                                            className="docpro_audit_progress_in docpro_audit_progress_in_3 maximizing_progress_3 docpro_fw_600 docpro_fs_14 docpro_primary_color"
                                            style={{ width: `${
                                              item?.annual_wellness_completed_visit_percentage
                                                ? item.annual_wellness_completed_visit_percentage
                                                : 0
                                              }%` }}
                                            >
                                            <span>
                                              {!editTransitionalCare ? 
                                                <>{item?.annual_wellness_completed_visit_percentage ? item?.annual_wellness_completed_visit_percentage : 0}%</>
                                              :
                                                <span className="docpro_signout_input">
                                                  <input
                                                    type="text"
                                                    className="docpro_fw_500"
                                                    style={{ maxWidth: "40px", minWidth: "40px", backgroundColor: "transparent", color: "#FFFFFF" }}
                                                    value={formData?.practice_opportunities[index]?.annual_wellness_completed_visit_percentage || 0}
                                                    onChange={(e) => handleTransitionalCareChange(index, "annual_wellness_completed_visit_percentage", parseFloat(e.target.value))}
                                                  />%
                                                </span>
                                              }
                                            </span>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        {!editTransitionalCare ? 
                                          <>${numberWithCommas((item?.annual_wellness_completed_visit || 0) * (transitionalCareData?.every_encounter_amount || 0))}</>
                                        :
                                          <>${numberWithCommas((formData?.practice_opportunities[index]?.annual_wellness_completed_visit || 0) * (transitionalCareData?.every_encounter_amount || 0))}</>
                                        }
                                      </td>
                                      <td>
                                        {!editTransitionalCare ? 
                                          <>{item?.potential_annual_wellness_completed_visit ? numberWithCommas(item?.potential_annual_wellness_completed_visit) : 0}</>
                                        :
                                          <span className="docpro_signout_input">
                                            <input
                                              type="text"
                                              className="docpro_fw_500"
                                              style={{ maxWidth: "50px" }}
                                              value={formData?.practice_opportunities[index]?.potential_annual_wellness_completed_visit || 0}
                                              onChange={(e) => handleTransitionalCareChange(index, "potential_annual_wellness_completed_visit", parseInt(e.target.value))}
                                            />
                                          </span>
                                        }
                                      </td>
                                      <td>
                                        {!editTransitionalCare ? 
                                          <>${numberWithCommas((item?.potential_annual_wellness_completed_visit || 0) * (transitionalCareData?.every_encounter_amount || 0))}</>
                                        :
                                          <>${numberWithCommas((formData?.practice_opportunities[index]?.potential_annual_wellness_completed_visit || 0) * (transitionalCareData?.every_encounter_amount || 0))}</>
                                        }
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td className="no_data" colSpan="5">
                                      No data available
                                    </td>
                                  </tr>
                                )
                              ) : (
                                <tr>
                                  <td className="no_data" colSpan="5">
                                    Loading...
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}